import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Plus, UserPlus } from "lucide-react";
import type { CircleListResponse, CircleSummary, UsersResponse } from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { EnrollmentActions } from "../components/enrollment-actions";
import { StrugglingCard } from "../components/struggling";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { CreateUserModal } from "../components/user-forms";
import { api, errorText, send } from "../lib/api";
import { genderLabel } from "../lib/labels";
import { useCan } from "../lib/org";
import { useMe } from "../lib/session";
import { CircleForm } from "./circles";

export function CirclePage() {
  const { circleId } = useParams({ from: "/app/circles/$circleId" });
  const { data: me } = useMe();
  const can = useCan();
  const qc = useQueryClient();
  const [modal, setModal] = useState<"edit" | "teacher" | "newStudent" | "existingStudent" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "with-archived"],
    queryFn: () => api<CircleListResponse>("/v1/circles?includeArchived=true"),
  });
  const students = useQuery({
    queryKey: ["users", me?.activeRole, "circle", circleId],
    queryFn: () => api<UsersResponse>(`/v1/users?circleId=${circleId}&role=student`),
  });
  const circle = circles.data?.circles.find((c) => c.id === circleId);

  if (circles.isPending) return <Loading />;
  if (!circle)
    return (
      <section className="card">
        <Empty title="الحلقة غير موجودة أو خارج نطاقك">
          <Link to="/circles">العودة إلى الحلقات</Link>
        </Empty>
      </section>
    );

  async function endTeacher(teacherId: string) {
    setActionError(null);
    try {
      await send("POST", `/v1/circles/${circleId}/teachers/${teacherId}/end`);
      await qc.invalidateQueries({ queryKey: ["circles"] });
    } catch (e) {
      setActionError(errorText(e));
    }
  }

  async function toggleArchive(c: CircleSummary) {
    setActionError(null);
    try {
      await send("PATCH", `/v1/circles/${c.id}`, { archived: !c.archivedAt });
      await qc.invalidateQueries({ queryKey: ["circles"] });
    } catch (e) {
      setActionError(errorText(e));
    }
  }

  const active = (students.data?.users ?? []).map((u) => ({
    user: u,
    enrollment: u.enrollments.find((e) => e.circleId === circleId && e.status === "active"),
  }));

  return (
    <>
      <PageTitle
        title={circle.name}
        sub={`${circle.branchName}${circle.programName ? ` · ${circle.programName}` : ""}`}
        actions={
          can.has("circles.manage") && (
            <>
              <button className="btn btn-secondary" onClick={() => setModal("edit")}>
                تعديل الحلقة
              </button>
              <button className="btn btn-danger" onClick={() => void toggleArchive(circle)}>
                {circle.archivedAt ? "إلغاء الأرشفة" : "أرشفة"}
              </button>
            </>
          )
        }
      />
      <ErrorNote error={actionError} />
      <div className="grid-detail">
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <section className="card">
            <div className="card-h">
              <h2>بيانات الحلقة</h2>
              {circle.archivedAt && <Badge>مؤرشفة</Badge>}
            </div>
            <dl className="kv">
              <dt>الفئة</dt>
              <dd>{circle.gender ? genderLabel[circle.gender] : "مختلطة / غير محددة"}</dd>
              <dt>الطلاب</dt>
              <dd className="num">
                {circle.studentCount}
                {circle.capacity ? ` من ${circle.capacity}` : ""}
              </dd>
              <dt>البرنامج</dt>
              <dd>{circle.programName ?? "—"}</dd>
            </dl>
          </section>
          <section className="card">
            <div className="card-h">
              <h2>المعلمون</h2>
              {can.has("circles.manage") && !circle.archivedAt && (
                <button className="btn btn-ghost btn-sm" onClick={() => setModal("teacher")}>
                  <Plus size={16} strokeWidth={1.75} /> إسناد معلم
                </button>
              )}
            </div>
            {circle.teachers.length === 0 ? (
              <Empty title="لا يوجد معلم مسند">لا يستطيع الطلاب طلب التسميع المباشر حتى يُسند معلم.</Empty>
            ) : (
              <ul className="list">
                {circle.teachers.map((t) => (
                  <li key={t.id}>
                    <Link to="/users/$userId" params={{ userId: t.id }}>
                      {t.name}
                    </Link>
                    {can.has("circles.manage") && (
                      <button className="btn btn-ghost btn-sm" onClick={() => void endTeacher(t.id)}>
                        إنهاء الإسناد
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        <StrugglingCard circleId={circleId} />
        <section className="card">
          <div className="card-h">
            <h2>الطلاب</h2>
            {can.has("enrollments.manage") && !circle.archivedAt && (
              <div className="row-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => setModal("existingStudent")}>
                  تسجيل طالب حالي
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal("newStudent")}>
                  <UserPlus size={16} strokeWidth={1.75} /> طالب جديد
                </button>
              </div>
            )}
          </div>
          {students.isPending ? (
            <Loading />
          ) : active.length === 0 ? (
            <Empty title="لا يوجد طلاب في الحلقة" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الطالب</th>
                    <th>الدخول</th>
                    <th>منذ</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {active.map(({ user, enrollment }) => (
                    <tr key={user.id}>
                      <td>
                        <Link to="/users/$userId" params={{ userId: user.id }}>
                          {user.name}
                        </Link>{" "}
                        {user.status !== "active" && <Badge tone="danger">موقوف</Badge>}
                      </td>
                      <td>
                        <span className="ltr muted small">{displayPhone(user.phone) || "—"}</span>
                      </td>
                      <td className="num small">{enrollment?.startedOn ?? "—"}</td>
                      <td className="actions">
                        {enrollment && me?.activeRole !== "teacher" && (
                          <Link
                            className="btn btn-ghost btn-sm"
                            to="/plan-preview"
                            search={{ enrollmentId: enrollment.id }}
                          >
                            معاينة الخطة
                          </Link>
                        )}
                        {can.has("enrollments.manage") && enrollment && (
                          <EnrollmentActions
                            enrollment={enrollment}
                            studentName={user.name}
                            studentGender={user.gender}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <CircleForm open={modal === "edit"} onClose={() => setModal(null)} circle={circle} />
      <AssignTeacherModal open={modal === "teacher"} onClose={() => setModal(null)} circle={circle} />
      <CreateUserModal
        open={modal === "newStudent"}
        onClose={() => setModal(null)}
        role="student"
        circle={circle}
      />
      <EnrollExistingModal
        open={modal === "existingStudent"}
        onClose={() => setModal(null)}
        circle={circle}
      />
    </>
  );
}

function AssignTeacherModal({
  open,
  onClose,
  circle,
}: {
  open: boolean;
  onClose: () => void;
  circle: CircleSummary;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { busy, error, run } = useSubmit();
  const [teacherId, setTeacherId] = useState("");
  const teachers = useQuery({
    queryKey: ["users", me?.activeRole, "teachers"],
    queryFn: () => api<UsersResponse>("/v1/users?role=teacher&status=active"),
    enabled: open,
  });
  const assigned = new Set(circle.teachers.map((t) => t.id));
  const choices = (teachers.data?.users ?? []).filter((u) => !assigned.has(u.id));
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/circles/${circle.id}/teachers`, { teacherId });
      await qc.invalidateQueries({ queryKey: ["circles"] });
      setTeacherId("");
      onClose();
    }, errorText);
  }
  return (
    <Modal title={`إسناد معلم إلى ${circle.name}`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field
          id="a-teacher"
          label="المعلم"
          hint={
            circle.gender
              ? `الحلقة مخصصة لـ${genderLabel[circle.gender]}؛ يلزم أن يكون جنس المعلم محددًا ومطابقًا`
              : undefined
          }
        >
          <select id="a-teacher" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} required>
            <option value="">اختر المعلم</option>
            {choices.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
                {u.gender ? "" : " (الجنس غير محدد)"}
              </option>
            ))}
          </select>
        </Field>
        {teachers.data && choices.length === 0 && (
          <p className="notice small">لا يوجد معلمون متاحون. أنشئ حساب معلم من صفحة المستخدمين أولًا.</p>
        )}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !teacherId}>
            {busy ? "جارٍ الإسناد…" : "إسناد"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EnrollExistingModal({
  open,
  onClose,
  circle,
}: {
  open: boolean;
  onClose: () => void;
  circle: CircleSummary;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { busy, error, run } = useSubmit();
  const [q, setQ] = useState("");
  const [studentId, setStudentId] = useState("");
  const results = useQuery({
    queryKey: ["users", me?.activeRole, "student-search", q],
    queryFn: () => api<UsersResponse>(`/v1/users?role=student&status=active&q=${encodeURIComponent(q)}`),
    enabled: open,
  });
  // Students already active in another circle are transferred instead (from their page).
  const choices = (results.data?.users ?? []).filter(
    (u) => !u.enrollments.some((e) => e.status === "active"),
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", "/v1/enrollments", { studentId, circleId: circle.id });
      await Promise.all(["users", "circles", "overview"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      setStudentId("");
      onClose();
    }, errorText);
  }
  return (
    <Modal title={`تسجيل طالب في ${circle.name}`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field
          id="x-q"
          label="بحث"
          hint="الطلاب غير المسجلين حاليًا في أي حلقة. لنقل طالب مسجل استخدم «نقل»."
        >
          <input
            id="x-q"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="الاسم أو اسم المستخدم أو الجوال"
          />
        </Field>
        <Field id="x-student" label="الطالب">
          <select id="x-student" value={studentId} onChange={(e) => setStudentId(e.target.value)} required>
            <option value="">اختر الطالب</option>
            {choices.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} {u.phone ? `(${displayPhone(u.phone)})` : ""}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !studentId}>
            {busy ? "جارٍ التسجيل…" : "تسجيل"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
